import { addDoc, collection, onSnapshot, query, serverTimestamp, where } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { SignOff } from '../types';
import { logAuditEvent } from '../hooks/useAuditLog';

// Live subscription to all sign-offs for an application (every sequence).
export function subscribeToSignoffs(appId: string, callback: (signoffs: SignOff[]) => void): () => void {
  const q = query(collection(db, 'signoffs'), where('appId', '==', appId));
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          appId: data.appId,
          sequenceId: data.sequenceId,
          userUid: data.userUid,
          userEmail: data.userEmail,
          meaning: data.meaning,
          comment: data.comment || undefined,
          signedAt: data.signedAt?.toDate() || new Date(),
        } as SignOff;
      });
      items.sort((a, b) => b.signedAt.getTime() - a.signedAt.getTime());
      callback(items);
    },
    (error) => {
      console.error('Sign-off subscription failed:', error);
      callback([]);
    }
  );
}

// Electronic sign-off: re-authenticates the signer with their password
// (signature manifestation), then writes an immutable sign-off record and
// an audit event. Throws on wrong password.
export async function signOffSequence(params: {
  appId: string;
  sequenceId: string;
  meaning: SignOff['meaning'];
  comment?: string;
  user: { uid: string; email: string };
  password: string;
}): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not signed in');

  // Signature manifestation: the signer must re-enter their password
  const credential = EmailAuthProvider.credential(params.user.email, params.password);
  await reauthenticateWithCredential(currentUser, credential);

  await addDoc(collection(db, 'signoffs'), {
    appId: params.appId,
    sequenceId: params.sequenceId,
    userUid: params.user.uid,
    userEmail: params.user.email,
    meaning: params.meaning,
    comment: params.comment || null,
    signedAt: serverTimestamp(),
  });

  await logAuditEvent(params.user, 'review_signoff', {
    applicationId: params.appId,
    sequenceId: params.sequenceId,
    meaning: params.meaning,
    comment: params.comment || '',
    reauthenticated: true,
  });
}
